/**
 * Formatação em pt-BR, desde o primeiro dia.
 *
 * `toFixed` devolve ponto decimal, e em português ponto lê como separador de
 * milhar: `1.5` vira "mil e quinhentos" no olho de quem lê a fila. Trocar de
 * idioma no meio da pilha é onde bug de tradução nasce — por isso nenhuma tela
 * formata número na mão.
 */

/**
 * Ausência de valor. Não é enfeite: `0%` onde o denominador não existe é
 * mentira, e "ninguém confirmou" e "ninguém foi convidado" são estados
 * diferentes.
 */
export const TRACO = '—';

const LOCAL = 'pt-BR';

/** `Intl.*Format` é caro de construir; memorizar por chave paga rápido numa
 *  tabela de mil linhas. */
const memoNumero = new Map<string, Intl.NumberFormat>();
const memoData = new Map<string, Intl.DateTimeFormat>();

function formatadorDeNumero(opcoes: Intl.NumberFormatOptions): Intl.NumberFormat {
  const chave = JSON.stringify(opcoes);
  let f = memoNumero.get(chave);
  if (!f) {
    f = new Intl.NumberFormat(LOCAL, opcoes);
    memoNumero.set(chave, f);
  }
  return f;
}

function formatadorDeData(opcoes: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const chave = JSON.stringify(opcoes);
  let f = memoData.get(chave);
  if (!f) {
    f = new Intl.DateTimeFormat(LOCAL, opcoes);
    memoData.set(chave, f);
  }
  return f;
}

/** Aceita o que o Postgres devolve (`timestamptz` em texto), `Date` e epoch. */
export type Instante = string | number | Date | null | undefined;

function paraData(v: Instante): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Número inteiro ou com casas fixas. `null`/`undefined`/`NaN` viram `—`:
 * contagem que ainda não chegou não é zero.
 */
export function num(valor: number | null | undefined, casas = 0): string {
  if (valor === null || valor === undefined || !Number.isFinite(valor)) return TRACO;
  return formatadorDeNumero({
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(valor);
}

/** `dd/mm/aaaa`. Fuso do dispositivo — o organizador opera no dele. */
export function data(valor: Instante): string {
  const d = paraData(valor);
  if (!d) return TRACO;
  return formatadorDeData({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(d);
}

/** `hh:mm`, 24h — que é como o Brasil escreve horário de evento. */
export function hora(valor: Instante): string {
  const d = paraData(valor);
  if (!d) return TRACO;
  return formatadorDeData({ hour: '2-digit', minute: '2-digit', hour12: false }).format(d);
}

/** `dd/mm/aaaa às hh:mm`. */
export function dataHora(valor: Instante): string {
  const d = paraData(valor);
  if (!d) return TRACO;
  return `${data(d)} às ${hora(d)}`;
}

/**
 * Porcentagem a partir de uma FRAÇÃO (0–1), que é como as funções de agregação
 * do banco devolvem taxa.
 *
 * `porcento(null)` é `—`, nunca `0%`: taxa sem denominador devolve `null` no
 * SQL de propósito, e transformar isso em zero na tela apaga a diferença que o
 * banco se deu ao trabalho de preservar. É regra de produto, não detalhe de
 * formatação.
 */
export function porcento(fracao: number | null | undefined, casas = 0): string {
  if (fracao === null || fracao === undefined || !Number.isFinite(fracao)) return TRACO;
  return formatadorDeNumero({
    style: 'percent',
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(fracao);
}

/**
 * O e-mail, mascarado para caber numa tela que outra pessoa pode estar vendo.
 *
 * `renato.barbosa@startse.com` → `re***@startse.com`
 *
 * ── Por que mascarar, e por que MOSTRAR alguma coisa ────────────────────────
 *
 * A tela final de um link aberto diz "esta resposta já foi registrada" para quem
 * volta. Sem nenhuma pista de QUEM respondeu, a frase é inútil para o caso que
 * importa: o celular emprestado, onde a resposta é de outra pessoa e quem está
 * olhando precisa reconhecer isso para clicar em "não sou eu".
 *
 * Mostrar o e-mail inteiro resolveria — e entregaria o endereço de um terceiro a
 * quem pegou o aparelho. As duas primeiras letras e o domínio bastam para a
 * pessoa se reconhecer (ou não) sem revelar o endereço a quem não o tem.
 *
 * ── As bordas, e por que nenhuma delas revela mais ──────────────────────────
 *
 * A parte local pode ser curta demais para esconder: `ab@x.com` mascarado como
 * `ab***@x.com` mostraria a parte local INTEIRA. Com 3 caracteres ou menos, só a
 * primeira letra fica. Com 1, nenhuma — `***@x.com`, que é o máximo de discrição
 * possível sem apagar o domínio, que é justamente o que ajuda a reconhecer.
 *
 * O que não parece e-mail volta mascarado por inteiro, e não como está: esta
 * função é chamada com o que o convidado digitou, e o campo `email` do produto
 * valida formato mas o valor chega do banco como texto livre.
 */
export function emailComMascara(valor: string | null | undefined): string {
  const texto = (valor ?? '').trim();
  if (texto === '') return TRACO;

  const arroba = texto.lastIndexOf('@');
  // Sem arroba não é endereço, e devolver o texto como está exporia o que quer
  // que ele seja. Três asteriscos dizem "há algo aqui" sem dizer o quê.
  if (arroba <= 0 || arroba === texto.length - 1) return '***';

  const local = texto.slice(0, arroba);
  const dominio = texto.slice(arroba);
  const visiveis = local.length <= 3 ? Math.min(1, local.length - 1) : 2;

  return `${local.slice(0, visiveis)}***${dominio}`;
}

/**
 * Junta partes numa enumeração legível: `a`, `a e b`, `a, b e c`.
 *
 * Existe porque a alternativa que aparece sozinha é `partes.join(', ')`, que
 * escreve "faltam VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY" — e a tela que
 * recusa subir é justamente a que não pode soar como despejo de log.
 */
export function frase(partes: readonly string[], conjuncao = 'e'): string {
  const limpas = partes.map((p) => p.trim()).filter((p) => p !== '');
  if (limpas.length === 0) return '';
  if (limpas.length === 1) return limpas[0] as string;
  const ultima = limpas[limpas.length - 1] as string;
  return `${limpas.slice(0, -1).join(', ')} ${conjuncao} ${ultima}`;
}
